<main>
  <header>
    <h1>{{ 'Reprocess_Title' | translateWF: 'Roll Reprocessing' }}</h1>
  </header>

  <div class="content">
    <p-stepper [value]="activeStep()" (valueChange)="onStepChange($event)">
      <!-- Step 1: Select Production Card & Rolls -->
      <p-step-item [value]="1">
        <p-step>{{ 'Reprocess_Step_SelectRolls' | translateWF: 'Select Rolls' }}</p-step>
        <p-step-panel>
          <ng-template #content let-activateCallback="activateCallback">
            <div class="step-content">
              <p-panel [header]="'Reprocess_SelectProductionCardAndRolls' | translateWF: 'Select Production Card and Rolls'">
                <p-card>
                  <!-- Production Card Search -->
                  <div class="production-card-search mb-4">
                    <label class="block mb-2 font-medium">Production Card</label>
                    <p-autoComplete
                      #productionCardSearch="cslServerSearch"
                      [cslServerSearch]="fetchProductionCards"
                      [suggestions]="productionCardSearch.results()"
                      (onSelect)="onProductionCardSelected($event.value)"
                      (onClear)="clearProductionCard()"
                      field="productionCardNumber"
                      [placeholder]="'Reprocess_SearchPlaceholder' | translateWF: 'Search production card...'"
                      [showClear]="true"
                      appendTo="body"
                      styleClass="w-full"
                    />
                  </div>

                  @if (selectedProductionCard()) {
                    <!-- Production Card Info -->
                    <div class="production-card-info mb-4 p-3 bg-slate-100 rounded-md">
                      <div class="grid-3">
                        <div>
                          <span class="text-gray-500 text-sm">Production Card</span>
                          <p class="font-medium">{{ selectedProductionCard()?.productionCardNumber }}</p>
                        </div>
                        <div>
                          <span class="text-gray-500 text-sm">{{ 'Reprocess_WorkOrder' | translateWF: 'Work Order' }}</span>
                          <p class="font-medium">{{ workOrder()?.orderNumber || '-' }}</p>
                        </div>
                        <div>
                          <span class="text-gray-500 text-sm">{{ 'Color' | translateWF: 'Color' }}</span>
                          <p class="font-medium">{{ color()?.name || '-' }}</p>
                        </div>
                      </div>
                    </div>

                    <!-- Whole Card Toggle -->
                    <div class="whole-card-toggle mb-4">
                      <div class="flex items-center gap-2">
                        <p-checkbox [formControl]="isWholeCardReprocessControl" [binary]="true" inputId="wholeCard" (onChange)="onWholeCardToggle()" />
                        <label for="wholeCard" class="font-medium">{{ 'Reprocess_ReprocessEntireCard' | translateWF: 'Reprocess entire production card' }}</label>
                      </div>
                    </div>

                    <!-- Toggle Table & Filter -->
                    <div class="flex items-center gap-4 mb-3">
                      <p-button
                        [label]="showRollsTable() ? ('Reprocess_HideRolls' | translateWF: 'Hide Rolls') : ('Reprocess_ShowRolls' | translateWF: 'Show Rolls')"
                        [icon]="showRollsTable() ? 'pi pi-eye-slash' : 'pi pi-eye'"
                        [outlined]="true"
                        size="small"
                        (onClick)="toggleRollsTable()"
                      />
                      @if (showRollsTable()) {
                        <span class="p-input-icon-left">
                          <i class="pi pi-search"></i>
                          <input
                            type="text"
                            pInputText
                            [placeholder]="'Reprocess_FilterByRollNumber' | translateWF: 'Filter by roll number...'"
                            [formControl]="rollNumberFilterControl"
                            class="p-inputtext-sm"
                          />
                        </span>
                      }
                    </div>

                    <!-- Rolls Summary (when table hidden) -->
                    @if (!showRollsTable() && !isLoadingRolls()) {
                      <div class="rolls-summary p-3 bg-slate-50 rounded-md mb-3">
                        <div class="flex items-center justify-between">
                          <span class="font-medium">
                            <i class="pi pi-list mr-2"></i>
                            {{ selectedRolls().length }} / {{ rolls().length }} {{ 'Reprocess_Rolls' | translateWF: 'rolls' }}
                            {{ 'Selected' | translateWF: 'selected' }}
                          </span>
                          <span class="text-gray-600"> {{ getTotalSelectedWeight() | number: '1.0-2' }} lb </span>
                        </div>
                      </div>
                    }

                    <!-- Rolls Table -->
                    @if (isLoadingRolls()) {
                      <div class="p-4">
                        <p-skeleton height="2rem" styleClass="mb-2" />
                        <p-skeleton height="2rem" styleClass="mb-2" />
                        <p-skeleton height="2rem" />
                      </div>
                    } @else if (showRollsTable()) {
                      <app-rolls-display-table
                        [rolls]="filteredRolls()"
                        [selectedRolls]="selectedRolls()"
                        [viewMode]="'reprocess'"
                        [selectionDisabled]="isWholeCardReprocess()"
                        [disabledRollNumbers]="invalidLocationRollNumbers()"
                        [disabledRollTooltip]="'Reprocess_InvalidLocationTooltip' | translateWF: 'This roll is not in Finishing or Rolling, so it cannot be reprocessed'"
                        (selectionChange)="onRollSelectionChange($event)"
                      />
                    }

                    <!-- Rolls Outside Finishing/Rolling -->
                    @if (invalidLocationRolls().length > 0) {
                      <div class="invalid-locations-warning mt-3 p-3 bg-orange-50 border-l-4 border-orange-400 rounded-md">
                        <div class="flex items-start gap-2 text-orange-800">
                          <i class="pi pi-exclamation-triangle mt-1"></i>
                          <div>
                            <strong>
                              {{ invalidLocationRolls().length }}
                              {{
                                'Reprocess_InvalidLocationTitle'
                                  | translateWF
                                    : 'roll(s) cannot be reprocessed from their
                          current location'
                              }}
                            </strong>
                            <p class="text-sm mt-1">
                              {{
                                'Reprocess_InvalidLocationDescription'
                                  | translateWF
                                    : 'Only rolls in Finishing or Rolling can be reprocessed. Transfer them back to
                          Rolling first, then reprocess.'
                              }}
                            </p>
                            <p class="text-sm mt-1 break-words">{{ getInvalidLocationRollsLabel(invalidLocationRolls()) }}</p>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Selection Blocked -->
                    @if (selectedInvalidLocationRolls().length > 0) {
                      <div class="selection-blocked mt-3 p-3 bg-red-50 border-l-4 border-red-400 rounded-md">
                        <div class="flex items-start gap-2 text-red-800">
                          <i class="pi pi-ban mt-1"></i>
                          <div>
                            <strong>{{ 'Reprocess_SelectionBlockedTitle' | translateWF: 'Cannot continue' }}</strong>
                            <p class="text-sm mt-1">
                              @if (isWholeCardReprocess()) {
                                {{
                                  'Reprocess_SelectionBlockedWholeCard'
                                    | translateWF
                                      : 'The entire production card cannot be reprocessed while some of its rolls are outside
                          Finishing or Rolling. Uncheck "Reprocess entire production card" and pick only the eligible
                          rolls, or transfer these rolls back to Rolling first.'
                                }}
                              } @else {
                                {{
                                  'Reprocess_SelectionBlockedPartial'
                                    | translateWF
                                      : 'Remove the rolls that are outside
                          Finishing or Rolling from the selection to continue.'
                                }}
                              }
                            </p>
                            <p class="text-sm mt-1 break-words">{{ getInvalidLocationRollsLabel(selectedInvalidLocationRolls()) }}</p>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Distinct Locations Info -->
                    @if (distinctLocations().length > 0) {
                      <div class="locations-info mt-3 p-2 bg-blue-50 rounded-md">
                        <span class="text-sm text-blue-700">
                          <i class="pi pi-map-marker mr-1"></i>
                          {{ 'Reprocess_RollsFrom' | translateWF: 'Rolls from:' }} {{ getDistinctLocationsLabel() }}
                        </span>
                      </div>
                    }
                  }
                </p-card>

                <ng-template pTemplate="footer">
                  <p-message severity="secondary" variant="simple">
                    {{
                      'Reprocess_SearchDescription'
                        | translateWF
                          : 'Search for a production card and select the rolls
                    to reprocess.'
                    }}
                  </p-message>
                </ng-template>
              </p-panel>
              <p-divider />
              <div class="row-sm-start-center">
                <p-button
                  [label]="'Reprocess_Next' | translateWF: 'Next'"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  [disabled]="!canProceedToStep2()"
                  (onClick)="activateCallback(2)"
                />
              </div>
            </div>
          </ng-template>
        </p-step-panel>
      </p-step-item>

      <!-- Step 2: Reprocess Configuration -->
      <p-step-item [value]="2">
        <p-step [disabled]="!canProceedToStep2()">{{ 'Reprocess_Step_Configuration' | translateWF: 'Configuration' }}</p-step>
        <p-step-panel>
          <ng-template #content let-activateCallback="activateCallback">
            <div class="step-content">
              <p-panel>
                <ng-template pTemplate="header">
                  <div class="flex flex-col">
                    <h3>{{ 'Reprocess_ConfigurationTitle' | translateWF: 'Reprocess Configuration' }}</h3>
                    <p class="text-gray-600 mb-4">
                      {{
                        'Reprocess_ConfigurationDescription'
                          | translateWF
                            : 'Configure the reprocessing options. Rolls
                      always go through finishing.'
                      }}
                    </p>
                  </div>
                </ng-template>

                <p-card>
                  <!-- Dyeing Option -->
                  <div class="dyeing-option mb-4">
                    <div class="flex items-center gap-2 mb-3">
                      <p-checkbox [formControl]="includeDyeingControl" [binary]="true" inputId="includeDyeing" />
                      <label for="includeDyeing" class="font-medium">{{ 'Reprocess_IncludeDyeing' | translateWF: 'Include Dyeing' }}</label>
                    </div>

                    @if (includeDyeing()) {
                      <div class="dyeing-configuration p-4 bg-slate-50 rounded-md">
                        <!-- Dyeing Machine Selection -->
                        <div class="mb-4">
                          <label class="block mb-2 font-medium">{{
                            'Reprocess_DyeingMachine'
                              | translateWF
                                : 'Dyeing
                          Machine'
                          }}</label>
                          <p-select
                            [formControl]="selectedDyeingMachineControl"
                            [options]="dyeingMachines"
                            optionLabel="assetId"
                            [placeholder]="'Reprocess_SelectDyeingMachine' | translateWF: 'Select dyeing machine'"
                            styleClass="w-full"
                            [showClear]="true"
                          />
                        </div>

                        <!-- Dyeing Mode Selection -->
                        <label class="block mb-2 font-medium">{{ 'Reprocess_DyeingMode' | translateWF: 'Dyeing Mode' }}</label>
                        <div class="flex mb-4">
                          <div class="dyeing-option-card" [class.selected]="dyeingMode() === 'batching'" (click)="setDyeingMode('batching')">
                            <i class="pi pi-sync text-2xl mb-2"></i>
                            <strong>{{ 'Reprocess_SendToBatching' | translateWF: 'Send to Batching' }}</strong>
                            <p class="text-sm text-gray-600">
                              {{ 'Reprocess_ReturnToBatchingQueue' | translateWF: 'Return rolls to batching queue' }}
                            </p>
                          </div>
                          <div class="dyeing-option-card" [class.selected]="dyeingMode() === 'bundles'" (click)="setDyeingMode('bundles')">
                            <i class="pi pi-box text-2xl mb-2"></i>
                            <strong>{{ 'Reprocess_CreateBundles' | translateWF: 'Create Bundles' }}</strong>
                            <p class="text-sm text-gray-600">
                              {{ 'Reprocess_ManuallyAssignRolls' | translateWF: 'Manually assign rolls to bundles' }}
                            </p>
                          </div>
                        </div>

                        <!-- Bundle Assignment Section -->
                        @if (dyeingMode() === 'bundles') {
                          <div class="bundle-assignment-section">
                            <div class="flex justify-between items-center mb-3">
                              <label class="font-medium">{{
                                'Reprocess_AssignRollsToBundles'
                                  | translateWF
                                    : 'Assign Rolls to
                            Bundles'
                              }}</label>
                              <p-button [label]="'Reprocess_AddBundle' | translateWF: 'Add Bundle'" icon="pi pi-plus" size="small" (onClick)="addBundle()" />
                            </div>

                            <div class="bundle-grid">
                              <!-- Unassigned Rolls -->
                              <div class="unassigned-rolls-panel">
                                <h5>{{ 'Reprocess_UnassignedRolls' | translateWF: 'Unassigned Rolls' }} ({{ unassignedRolls.length }})</h5>
                                <div class="filter-input">
                                  <span class="p-input-icon-left w-full">
                                    <i class="pi pi-search"></i>
                                    <input
                                      type="text"
                                      pInputText
                                      [placeholder]="'Reprocess_FilterRolls' | translateWF: 'Filter rolls...'"
                                      [formControl]="unassignedRollFilterControl"
                                      class="p-inputtext-sm w-full"
                                    />
                                  </span>
                                </div>
                                <div class="roll-list">
                                  @for (roll of filteredUnassignedRolls; track roll.rollNumber) {
                                    <div class="roll-item" (click)="addRollToBundle(roll)">
                                      <span class="roll-number">{{ roll.rollNumber }}</span>
                                      <span class="roll-weight">{{ roll.weight | number: '1.0-2' }} lb</span>
                                      <i class="pi pi-plus-circle"></i>
                                    </div>
                                  }
                                  @if (filteredUnassignedRolls.length === 0 && unassignedRolls.length > 0) {
                                    <div class="empty-message">
                                      {{
                                        'Reprocess_NoRollsMatchFilter'
                                          | translateWF
                                            : 'No rolls
                                match filter'
                                      }}
                                    </div>
                                  }
                                  @if (unassignedRolls.length === 0) {
                                    <div class="empty-message">
                                      {{
                                        'Reprocess_AllRollsAssigned'
                                          | translateWF
                                            : 'All rolls
                                assigned'
                                      }}
                                    </div>
                                  }
                                </div>
                              </div>

                              <!-- Bundle Cards -->
                              <div class="bundles-container">
                                @for (bundle of bundles; track bundle.id) {
                                  <div class="bundle-card" [class.selected]="selectedBundleId === bundle.id" (click)="selectBundle(bundle.id)">
                                    <div class="bundle-header">
                                      <span class="bundle-name">{{ bundle.name }}</span>
                                      <p-button icon="pi pi-trash" severity="danger" [text]="true" size="small" (onClick)="removeBundle(bundle.id); $event.stopPropagation()" />
                                    </div>
                                    <div class="bundle-stats">
                                      <span>{{ bundle.rolls.length }} {{ 'Reprocess_Rolls' | translateWF: 'rolls' }}</span>
                                      <span class="bundle-weight">{{ getBundleTotalWeight(bundle) | number: '1.0-2' }} lb</span>
                                    </div>
                                    <div class="bundle-rolls">
                                      @for (roll of bundle.rolls; track roll.rollNumber) {
                                        <div class="bundle-roll-item">
                                          <span>{{ roll.rollNumber }}</span>
                                          <span class="text-gray-500">{{ roll.weight | number: '1.0-2' }} lb</span>
                                          <i class="pi pi-times-circle remove-roll" (click)="removeRollFromBundle(bundle.id, roll.rollNumber); $event.stopPropagation()"></i>
                                        </div>
                                      }
                                      @if (bundle.rolls.length === 0) {
                                        <div class="empty-bundle">
                                          {{
                                            'Reprocess_ClickToSelectThenAddRolls'
                                              | translateWF
                                                : 'Click to select, then add
                                  rolls'
                                          }}
                                        </div>
                                      }
                                    </div>
                                  </div>
                                }
                              </div>
                            </div>
                          </div>
                        }
                      </div>
                    }
                  </div>

                  <!-- Info Message -->
                  <p-message severity="info" styleClass="mb-4 cs-message-fit">
                    <span>
                      @if (includeDyeing()) {
                        {{
                          'Reprocess_RollsWillGoThrough_DyeingFinishing'
                            | translateWF
                              : 'Rolls will go through Dyeing →
                      Finishing'
                        }}
                      } @else {
                        {{
                          'Reprocess_RollsWillGoThrough_FinishingOnly'
                            | translateWF
                              : 'Rolls will go through Finishing
                      only'
                        }}
                      }
                    </span>
                  </p-message>
                </p-card>
              </p-panel>

              <p-divider />

              <div class="row-sm-start-center">
                <p-button [label]="'Reprocess_Back' | translateWF: 'Back'" icon="pi pi-arrow-left" severity="secondary" (onClick)="activateCallback(1)" />
                <p-button
                  [label]="'Reprocess_Next' | translateWF: 'Next'"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  [disabled]="!canProceedToStep3()"
                  (onClick)="activateCallback(3)"
                />
              </div>
            </div>
          </ng-template>
        </p-step-panel>
      </p-step-item>

      <!-- Step 3: Summary -->
      <p-step-item [value]="3">
        <p-step [disabled]="!canProceedToStep2()">{{ 'Reprocess_Step_Summary' | translateWF: 'Summary' }}</p-step>
        <p-step-panel>
          <ng-template #content let-activateCallback="activateCallback">
            <div class="step-content">
              <p-panel>
                <ng-template pTemplate="header">
                  <div class="flex flex-col">
                    <h3>{{ 'Reprocess_SummaryTitle' | translateWF: 'Reprocessing Summary' }}</h3>
                    <p class="text-gray-600 mb-4">
                      {{
                        'Reprocess_SummaryDescription'
                          | translateWF
                            : 'Review the reprocessing configuration before
                      confirming.'
                      }}
                    </p>
                  </div>
                </ng-template>

                <p-card>
                  <div class="summary-card">
                    <!-- Production Card Info -->
                    <div class="summary-section">
                      <h4><i class="pi pi-id-card mr-2"></i>Production Card</h4>
                      <p>
                        <strong>{{ selectedProductionCard()?.productionCardNumber }}</strong>
                      </p>
                      <p class="text-sm text-gray-600">
                        {{
                          isWholeCardReprocess()
                            ? ('Reprocess_EntireProductionCard' | translateWF: 'Entire production card')
                            : ('Reprocess_PartialReprocess' | translateWF: 'Partial reprocess')
                        }}
                      </p>
                    </div>

                    <!-- Rolls Info -->
                    <div class="summary-section">
                      <h4><i class="pi pi-list mr-2"></i>{{ 'Reprocess_SelectedRolls' | translateWF: 'Selected Rolls' }}</h4>
                      <p>
                        <strong>{{ getSelectedRollCount() }}</strong> {{ 'Reprocess_Rolls' | translateWF: 'rolls' }} ({{ getTotalSelectedWeight() | number: '1.0-2' }} lb
                        {{ 'total' | translateWF: 'total' }})
                      </p>
                      <div class="roll-chips mt-2 flex gap-1 flex-wrap">
                        @for (roll of selectedRolls(); track roll.number; let idx = $index) {
                          @if (idx < 5) {
                            <p-tag [value]="roll.number || ''" severity="secondary" />
                          }
                        }
                        @if (selectedRolls().length > 5) {
                          <p-tag [value]="'+' + (selectedRolls().length - 5) + ' ' + ('more' | translateWF: 'more')" severity="info" />
                        }
                      </div>
                    </div>

                    <!-- Process Flow -->
                    <div class="summary-section">
                      <h4><i class="pi pi-arrow-right-arrow-left mr-2"></i>{{ 'Reprocess_ProcessFlow' | translateWF: 'Process Flow' }}</h4>
                      <div class="flex gap-2 mt-2 items-center">
                        @if (includeDyeing()) {
                          <p-tag [value]="'Dyeing' | translateWF: 'Dyeing'" severity="warn" />
                          <i class="pi pi-arrow-right"></i>
                        }
                        <p-tag [value]="'Reprocess_Finishing' | translateWF: 'Finishing'" severity="success" />
                      </div>
                    </div>

                    <!-- Dyeing Details -->
                    @if (includeDyeing()) {
                      <div class="summary-section">
                        <h4>
                          <i class="pi pi-cog mr-2"></i
                          >{{
                            'Reprocess_DyeingConfiguration'
                              | translateWF
                                : 'Dyeing
                        Configuration'
                          }}
                        </h4>
                        <p>
                          <strong>{{ 'Reprocess_Machine' | translateWF: 'Machine:' }}</strong>
                          {{ selectedDyeingMachine()?.assetId || ('Reprocess_NotSelected' | translateWF: 'Not selected') }}
                        </p>
                        <p>
                          <strong>{{ 'Reprocess_Mode' | translateWF: 'Mode:' }}</strong>
                          {{
                            dyeingMode() === 'bundles'
                              ? ('Reprocess_CreateBundles' | translateWF: 'Create Bundles')
                              : ('Reprocess_SendToBatching' | translateWF: 'Send to Batching')
                          }}
                        </p>
                        @if (dyeingMode() === 'bundles') {
                          <div class="summary-bundles mt-2">
                            @for (bundle of bundles; track bundle.id) {
                              <div class="summary-bundle-item">
                                <span
                                  >{{ bundle.name }}: {{ bundle.rolls.length }} {{ 'Reprocess_Rolls' | translateWF: 'rolls' }} ({{
                                    getBundleTotalWeight(bundle) | number: '1.0-2'
                                  }}
                                  lb)</span
                                >
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }

                    <!-- What will happen -->
                    <div class="summary-section mt-4">
                      <h4>
                        <i class="pi pi-info-circle mr-2"></i
                        >{{
                          'Reprocess_WhatWillHappen'
                            | translateWF
                              : 'What will
                        happen'
                        }}
                      </h4>
                      <ul class="list-disc pl-5 text-gray-700">
                        @if (isWholeCardReprocess()) {
                          <li>
                            {{
                              'Reprocess_CardWillBeUpdatedWithSuffix'
                                | translateWF
                                  : 'Production card will be updated
                          with reprocess suffix (e.g., -R1)'
                            }}
                          </li>
                        } @else {
                          <li>
                            {{
                              'Reprocess_NewCardWillBeCreated'
                                | translateWF
                                  : 'A new production card will be created for
                          the reprocessed rolls'
                            }}
                          </li>
                          <li>
                            {{
                              'Reprocess_OriginalCardWillContinue'
                                | translateWF
                                  : 'Original card will continue with
                          remaining rolls'
                            }}
                          </li>
                        }
                        @if (includeDyeing()) {
                          @if (dyeingMode() === 'bundles') {
                            <li>
                              {{ bundles.length }} Bundle(s)
                              {{ 'Reprocess_BundlesWillBeCreatedSuffix' | translateWF: 'will be created for dyeing' }}
                            </li>
                          } @else {
                            <li>
                              {{
                                'Reprocess_RollsWillBeSentToBatching'
                                  | translateWF
                                    : 'Rolls will be sent to batching queue
                          for dyeing'
                              }}
                            </li>
                          }
                          <li>
                            {{
                              'Reprocess_AfterDyeingRollsWillProceed'
                                | translateWF
                                  : 'After dyeing, rolls will proceed
                          to finishing'
                            }}
                          </li>
                        } @else {
                          <li>
                            {{
                              'Reprocess_RollsWillGoDirectlyToFinishing'
                                | translateWF
                                  : 'Rolls will go directly to
                          finishing'
                            }}
                          </li>
                        }
                      </ul>
                    </div>
                  </div>
                </p-card>
              </p-panel>
              <p-divider />
              <div class="row-sm-start-center">
                <p-button [label]="'Reprocess_Back' | translateWF: 'Back'" icon="pi pi-arrow-left" severity="secondary" (onClick)="activateCallback(2)" />
                <p-button
                  [label]="'Reprocess_ConfirmReprocessing' | translateWF: 'Confirm Reprocessing'"
                  icon="pi pi-check"
                  severity="success"
                  [loading]="isSubmitting()"
                  [disabled]="!canProceedToStep2()"
                  (onClick)="confirmReprocessing()"
                />
              </div>
            </div>
          </ng-template>
        </p-step-panel>
      </p-step-item>

      <!-- Step 4: Result Summary -->
      <p-step-item [value]="4">
        <p-step>{{ 'Reprocess_Step_Result' | translateWF: 'Resultado' }}</p-step>
        <p-step-panel>
          <ng-template #content>
            <div class="step-content result-summary">
              <p-panel>
                <!-- Success header -->
                <ng-template pTemplate="header">
                  <div class="result-header">
                    <i class="pi pi-check-circle result-icon"></i>
                    <h3>{{ 'Reprocess_Result_Title' | translateWF: 'Reproceso completado exitosamente' }}</h3>
                  </div>
                </ng-template>

                <p-card>
                  <!-- What happened -->
                  <div class="result-section">
                    <h4>{{ 'Reprocess_Result_WhatHappened' | translateWF: '¿Qué sucedió?' }}</h4>
                    @if (reprocessResult()?.scenario === 0) {
                      <p-message
                        severity="info"
                        [text]="'Reprocess_Result_FinishingOnly' | translateWF: 'Los rollos fueron consolidados en la ubicación de reproceso y enviados a acabado (en ruta).'"
                      />
                    }
                    @if (reprocessResult()?.scenario === 1) {
                      <p-message
                        severity="info"
                        [text]="'Reprocess_Result_DyeingToBatching' | translateWF: 'Los rollos fueron consolidados y enviados a batching para teñido (en ruta).'"
                      />
                    }
                    @if (reprocessResult()?.scenario === 2) {
                      <p-message
                        severity="info"
                        [text]="'Reprocess_Result_DyeingWithBundles' | translateWF: 'Los rollos fueron consolidados, asignados a bundles y enviados a batching.'"
                      />
                    }
                  </div>

                  <!-- Production card info -->
                  <div class="result-section">
                    <h4>{{ 'Reprocess_Result_ProductionCard' | translateWF: 'Tarjeta de producción' }}</h4>
                    @if (resultProductionCard()) {
                      <div class="result-card-info">
                        <span class="result-label">{{
                          reprocessResult()?.isWholeProductionCard
                            ? ('Reprocess_Result_UpdatedPC' | translateWF: 'Tarjeta actualizada:')
                            : ('Reprocess_Result_NewPC' | translateWF: 'Nueva tarjeta creada:')
                        }}</span>
                        <a class="result-pc-link" [routerLink]="['/production-card', resultProductionCard()!.productionCardNumber, 'details']">
                          {{ resultProductionCard()!.productionCardNumber }}
                        </a>
                        <p-tag [value]="getStatusDisplayName(resultProductionCard()!.status)" severity="info" />
                      </div>
                    } @else {
                      <p-skeleton width="200px" height="24px" />
                    }
                  </div>

                  <!-- Rolls processed -->
                  <div class="result-section">
                    <h4>{{ 'Reprocess_Result_Rolls' | translateWF: 'Rollos procesados' }}</h4>
                    <div class="result-rolls-list">
                      @for (roll of reprocessResult()?.processedRollNumbers ?? []; track roll) {
                        <p-tag [value]="roll" severity="secondary" />
                      }
                    </div>
                  </div>

                  <!-- Next steps -->
                  <div class="result-section result-next-steps">
                    <h4>{{ 'Reprocess_Result_NextSteps' | translateWF: 'Próximos pasos' }}</h4>
                    <ul>
                      @if (reprocessResult()?.scenario === 0) {
                        <li>
                          {{
                            'Reprocess_NextStep_Finishing'
                              | translateWF
                                : 'Cambiar el estado de la tarjeta a "Finishing" para crear las rutas de acabado y
                        recibir los rollos en la ubicación de acabado.'
                          }}
                        </li>
                      }
                      @if (reprocessResult()?.scenario === 1) {
                        <li>
                          {{
                            'Reprocess_NextStep_Batching1'
                              | translateWF
                                : 'Cambiar el estado de la tarjeta a "Batching"
                        para recibir los rollos en batching.'
                          }}
                        </li>
                        <li>{{ 'Reprocess_NextStep_Batching2' | translateWF: 'Crear los bundles para el teñido.' }}</li>
                      }
                      @if (reprocessResult()?.scenario === 2) {
                        <li>
                          {{
                            'Reprocess_NextStep_Bundles'
                              | translateWF
                                : 'Los bundles ya fueron creados. La tarjeta está
                        lista para continuar el proceso de teñido.'
                          }}
                        </li>
                      }
                    </ul>
                  </div>
                </p-card>
              </p-panel>
              <p-divider />
              <!-- Actions -->
              <div class="row-sm-start-center">
                @if (resultProductionCard()) {
                  <a [routerLink]="['/production-card', resultProductionCard()!.productionCardNumber, 'details']">
                    <p-button [label]="'Reprocess_Result_GoToPC' | translateWF: 'Ir a la tarjeta'" icon="pi pi-external-link" />
                  </a>
                }
                <p-button [label]="'Reprocess_Result_NewReprocess' | translateWF: 'Nuevo reproceso'" icon="pi pi-refresh" severity="secondary" (onClick)="resetForm()" />
              </div>
            </div>
          </ng-template>
        </p-step-panel>
      </p-step-item>
    </p-stepper>
  </div>
</main>
